import { useEffect, useState } from 'react'
import { Box, Group, Text, UnstyledButton, ScrollArea, Anchor, Paper, Badge, Drawer, Burger, Avatar } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

// #1080 AppNavbarはdata-turbolinks-permanentでページ遷移をまたいでDOM/Reactツリーが
// 維持される(application.jsxのmountReactComponentsが再マウントをスキップする)。
// そのため「どのセクションがアクティブか」はサーバーから渡されたpropsに固定せず、
// turbolinks:load毎に現在のpathnameを見て自前で再計算する。
function useCurrentPath() {
  const [path, setPath] = useState(() => window.location.pathname)

  useEffect(() => {
    const handleLoad = () => setPath(window.location.pathname)
    document.addEventListener('turbolinks:load', handleLoad)
    return () => document.removeEventListener('turbolinks:load', handleLoad)
  }, [])

  return path
}

// #1080 グローバルナビゲーション。Mantine UI「NavbarNested」のLinksGroup/UserButtonパターンを
// 参考にした縦型Navbar。表示可否・権限判定はRails側で計算し、ここではpropsをそのまま描画する。
function Icon({ name, size = 18 }) {
  return (
    <span className="material-icons" style={{ fontSize: size, lineHeight: 1 }}>
      {name}
    </span>
  )
}

function LinksGroup({ section, opened, onToggle }) {
  const hasChildren = section.links.length > 1

  if (!hasChildren) {
    const link = section.links[0]
    return (
      <Anchor
        href={link.href}
        underline="never"
        c="inherit"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '9px 12px',
          borderRadius: 'var(--mantine-radius-sm)',
          fontWeight: 600,
          fontSize: 14,
          background: section.active ? 'var(--mantine-color-brand-0)' : 'transparent',
          color: section.active ? 'var(--mantine-color-brand-7)' : 'var(--mantine-color-dark-6)',
        }}
      >
        <Icon name={section.icon} />
        <Box style={{ flex: 1 }}>{link.title}</Box>
        {!!section.badge && <Badge size="sm" color="red" circle>{section.badge}</Badge>}
      </Anchor>
    )
  }

  return (
    <Box>
      <UnstyledButton
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 10,
          padding: '9px 12px',
          borderRadius: 'var(--mantine-radius-sm)',
          fontWeight: 600,
          fontSize: 14,
          background: section.active ? 'var(--mantine-color-brand-0)' : 'transparent',
          color: section.active ? 'var(--mantine-color-brand-7)' : 'var(--mantine-color-dark-6)',
        }}
      >
        <Icon name={section.icon} />
        <Box style={{ flex: 1, textAlign: 'left' }}>{section.title}</Box>
        <span
          className="material-icons"
          style={{
            fontSize: 16,
            lineHeight: 1,
            transition: 'transform 150ms ease',
            transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          expand_more
        </span>
      </UnstyledButton>
      <Box
        style={{
          maxHeight: opened ? 300 : 0,
          overflow: 'hidden',
          transition: 'max-height 200ms ease',
        }}
      >
        <Box pl={30} pt={2}>
          {section.links.map((link) => (
            <Anchor
              key={link.href}
              href={link.href}
              underline="never"
              c="dimmed"
              style={{ display: 'block', padding: '7px 10px', fontSize: 13.5 }}
            >
              {link.title}
            </Anchor>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

function NavLinks({ sections }) {
  const currentPath = useCurrentPath()
  const sectionsWithActive = sections.map((section) => ({
    ...section,
    active: section.exactPath
      ? currentPath === section.exactPath
      : (section.pathPrefix ? currentPath.startsWith(section.pathPrefix) : section.active),
  }))

  const activeSection = sectionsWithActive.find((section) => section.active && section.links.length > 1)
  const [openId, setOpenId] = useState(activeSection ? activeSection.id : null)

  return (
    <Box>
      {sectionsWithActive.map((section) => (
        <LinksGroup
          key={section.id}
          section={section}
          opened={openId === section.id}
          onToggle={() => setOpenId((current) => (current === section.id ? null : section.id))}
        />
      ))}
    </Box>
  )
}

// Mantine UI「UserButton」パターン(Avatar + 名前/メール + シェブロン)を参考にした
// アカウントボタン。クリックでメニューを開き、プロフィール/ログアウトを表示する。
// 注: MantineのMenuコンポーネントはこの環境ではドロップダウンの中身が描画されない不具合が
// あったため(状態は切り替わるがDOM自体が生成されない)、Paperを使った手作りのポップアップにしている。
// 外側クリックで閉じる処理も、document.addEventListenerベースの実装がこの環境では効かなかったため、
// 透明な全画面オーバーレイのonClick(Reactの合成イベント)で閉じる方式にしている。
function UserButton({ profileLinks, currentUser }) {
  const [opened, setOpened] = useState(false)
  const initial = currentUser?.name?.trim()?.charAt(0) || '?'

  return (
    <Box style={{ position: 'relative' }}>
      {opened && (
        <Box
          onClick={() => setOpened(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 199 }}
        />
      )}
      <UnstyledButton
        onClick={() => setOpened((o) => !o)}
        style={{
          display: 'block',
          width: '100%',
          padding: 'var(--mantine-spacing-sm)',
          borderRadius: 'var(--mantine-radius-sm)',
        }}
      >
        <Group wrap="nowrap">
          <Avatar src={currentUser?.avatarUrl} radius="xl" alt={currentUser?.name}>
            {initial}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>{currentUser?.name}</Text>
            <Text c="dimmed" size="xs" truncate>{currentUser?.email}</Text>
          </Box>
          <Icon name="chevron_right" size={16} />
        </Group>
      </UnstyledButton>

      {opened && (
        <Paper
          shadow="md"
          radius="sm"
          withBorder
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)',
            left: 0,
            width: 220,
            padding: 4,
            zIndex: 200,
          }}
        >
          {profileLinks.map((link) => (
            <Anchor
              key={link.href}
              href={link.href}
              data-method={link.method}
              underline="never"
              c="var(--mantine-color-dark-6)"
              onClick={() => setOpened(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 'var(--mantine-radius-sm)',
                fontSize: 14,
              }}
            >
              <Icon name={link.icon} size={16} />
              {link.title}
            </Anchor>
          ))}
        </Paper>
      )}
    </Box>
  )
}

export default function AppNavbar({ logoHref, logoSrc, sections = [], profileLinks = [], secondaryLinks = [], currentUser }) {
  const [mobileOpened, { open: openMobile, close: closeMobile }] = useDisclosure(false)

  const navContent = (
    <>
      <Box
        style={{
          height: 60,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
      >
        <Anchor href={logoHref}>
          <img src={logoSrc} alt="Bizlink" height={28} />
        </Anchor>
      </Box>
      <ScrollArea style={{ flex: 1 }} p="sm">
        <NavLinks sections={sections} />
      </ScrollArea>
      <Box p="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
        <UserButton profileLinks={profileLinks} currentUser={currentUser} />
      </Box>
      {secondaryLinks.length > 0 && (
        <Box px="sm" py={4} style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          {secondaryLinks.map((link) => (
            <Anchor
              key={link.href}
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener' : undefined}
              underline="never"
              c="dimmed"
              style={{ display: 'block', padding: '4px 8px', fontSize: 12.5 }}
            >
              {link.title}
            </Anchor>
          ))}
        </Box>
      )}
    </>
  )

  return (
    <>
      {/* デスクトップ: 常時表示の左Navbar */}
      <Box
        component="nav"
        visibleFrom="sm"
        style={{
          width: 260,
          flexShrink: 0,
          height: '100vh',
          position: 'sticky',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid var(--mantine-color-gray-2)',
          background: '#fff',
        }}
      >
        {navContent}
      </Box>

      {/* モバイル: 上部バー+Drawer */}
      <Box
        component="header"
        hiddenFrom="sm"
        px="md"
        py={10}
        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Anchor href={logoHref}>
          <img src={logoSrc} alt="Bizlink" height={28} />
        </Anchor>
        <Burger opened={mobileOpened} onClick={openMobile} aria-label="メニューを開く" />
      </Box>
      <Drawer opened={mobileOpened} onClose={closeMobile} position="right" size="85%" padding={0} withCloseButton={false}>
        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          <UnstyledButton
            onClick={closeMobile}
            aria-label="メニューを閉じる"
            style={{
              position: 'absolute',
              top: 14,
              right: 12,
              zIndex: 1,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--mantine-radius-sm, 4px)',
              color: 'var(--mantine-color-gray-6, #868e96)',
            }}
          >
            <span className="material-icons" aria-hidden="true">close</span>
          </UnstyledButton>
          {navContent}
        </Box>
      </Drawer>
    </>
  )
}
